/**
 * The rules, with no I/O in them.
 *
 * Everything here runs identically in Deno (the edge functions) and in Node
 * (the test suite), which is the point: the transition table that decides
 * whether a routine may mark something merged is the same code that the tests
 * pin down.
 */

/* ── the ladder ─────────────────────────────────────────────────────────── */

export const LADDER = ['fresh', 'working', 'signed_off', 'approved', 'merged', 'deployed'] as const;
export type LadderStatus = (typeof LADDER)[number];

/**
 * `error` is not a rung: work falls into it from anywhere and climbs back out.
 *
 * There is deliberately no `previewable`. A task is reviewable when it has a
 * preview link — so a routine cannot announce readiness without actually
 * handing you something to open.
 */
export const TASK_STATUSES = [...LADDER, 'error'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Statuses a routine may report over the callback API. */
export const AGENT_REPORTABLE: readonly TaskStatus[] = [
  'working',
  'signed_off',
  'merged',
  'deployed',
  'error',
];

/** Landed, or on its way in with nothing left for a person to do. */
export const SETTLED_STATUSES: readonly TaskStatus[] = ['approved', 'merged', 'deployed'];

export function isTaskStatus(value: unknown): value is TaskStatus {
  return typeof value === 'string' && (TASK_STATUSES as readonly string[]).includes(value);
}

const forwardFrom = (status: LadderStatus): LadderStatus[] =>
  LADDER.slice(LADDER.indexOf(status) + 1);

/**
 * Any move *forward* is legal, because a routine reporting a later status is
 * reporting something that already happened in the world — refusing it would
 * only desync the board from reality. Moving *backward* only goes to `working`
 * (sent back for revisions). `deployed` is terminal.
 *
 * Re-reporting the same status is always fine; routines use it as a heartbeat.
 */
const TRANSITIONS: Record<TaskStatus, readonly TaskStatus[]> = {
  fresh: [...forwardFrom('fresh'), 'error'],
  working: [...forwardFrom('working'), 'error'],
  signed_off: ['working', ...forwardFrom('signed_off'), 'error'],
  approved: ['working', ...forwardFrom('approved'), 'error'],
  merged: ['working', ...forwardFrom('merged'), 'error'],
  deployed: [],
  error: [...LADDER],
};

export function canTransition(from: TaskStatus, to: TaskStatus): boolean {
  if (from === to) return true;
  return (TRANSITIONS[from] as readonly string[]).includes(to);
}

export class TransitionError extends Error {
  readonly status = 409;
  constructor(
    readonly from: TaskStatus,
    readonly to: TaskStatus,
  ) {
    super(`cannot move a task from "${from}" to "${to}"`);
    this.name = 'TransitionError';
  }
}

export function assertTransition(from: TaskStatus, to: TaskStatus): void {
  if (!canTransition(from, to)) throw new TransitionError(from, to);
}

/* ── what the routine says it is doing ──────────────────────────────────── */

/**
 * Orthogonal to the ladder, and the reason "Needs you" can be trusted.
 *
 * A task sits in `working` for its whole active life, so the ladder alone
 * cannot distinguish "still going" from "parked, waiting for a human". This
 * can, because the routine says so out loud.
 */
/**
 * `answered` is the one state a routine never reports: Rigg sets it when a
 * person replies to a routine that was waiting on them. It means the question
 * has been answered and the routine has not come back for it yet — which is
 * neither "waiting on you" nor "working", and was previously the former.
 */
export const AGENT_STATES = [
  'idle',
  'working',
  'ready_for_review',
  'waiting_on_you',
  'answered',
  'blocked',
  'done',
] as const;
export type AgentState = (typeof AGENT_STATES)[number];

/**
 * The states a routine may report, which is not all of them.
 *
 * `idle` is where a task starts and `answered` is what a person's reply leaves
 * behind — neither is a routine's to claim. Kept here rather than written out
 * in the brief so that adding a state forces a decision about which side of
 * this line it falls on.
 */
export const AGENT_REPORTABLE_STATES: readonly AgentState[] = [
  'working',
  'ready_for_review',
  'waiting_on_you',
  'blocked',
  'done',
] as const;

/**
 * Is this state one where the routine has stopped and is waiting on a person?
 *
 * `waiting_on_you` and `ready_for_review` are both handovers, and the
 * difference between them is the whole reason the second one exists: one is a
 * question, which a person can answer from the task itself, and the other is an
 * invitation to look at something, which they cannot act on until there is
 * something to look at. Treating them alike is how you get a queue entry that
 * says "your review" above a task with no preview and a red check — nothing a
 * person can do, filed under the heading of things only they can do.
 */
export function isHandover(state: AgentState): boolean {
  return state === 'waiting_on_you' || state === 'ready_for_review';
}

/**
 * What every routine is told about its own ship-time review, in the brief and
 * in every comment that approves, clears or nudges a pull request.
 *
 * Routines were refusing it. Their permission classifier read "review your own
 * diff, then report the sha that lets it merge" as an agent approving its own
 * work, and stopped — on work a person had already approved, under a review
 * the project owner requires. So it is said outright, everywhere a routine
 * might meet the question cold: approved, mandatory, and whose rule it is.
 */
export const REVIEW_MANDATE =
  '**The `approved` label is set by a person — never by you.** It is the signal that this pull ' +
  'request is approved, and that your job now is to make it ready to merge. When it is on: run ' +
  'the ship-time code review of your own diff, get the checks green, and report `reviewed_sha` ' +
  'for the head your review covered. That review is APPROVED AND MANDATORY — the project ' +
  "owner's own merge gate, explicitly sanctioned, not self-approval: the person who set the label " +
  'decided this may land, and the review is the quality check they require from you on top of ' +
  'that. Run it yourself, without asking anyone for permission, and do not hand it to another ' +
  'session. Do not merge the pull request yourself: Rigg merges once it is clean, green and ' +
  'reviewed at its head.';

/**
 * Whether a comment answers the routine — moving a task parked on a person to
 * `answered`.
 *
 * Only a person's own reply. Not a routine commenting on its own task, and not
 * anything Rigg posts: the merge driver's nudge has no user behind it, and an
 * approval is a person's notice that has not answered whatever was asked.
 * Counting the nudge is how #752 told its routine "answered" over a question
 * nobody had seen.
 */
export function commentAnswers(
  state: AgentState,
  author: { userId?: string | null; agentId?: string | null; answers?: boolean },
): boolean {
  if (author.agentId || !author.userId || author.answers === false) return false;
  return isHandover(state) || state === 'answered';
}

export function isAgentState(value: unknown): value is AgentState {
  return typeof value === 'string' && (AGENT_STATES as readonly string[]).includes(value);
}

/** Wording a routine might plausibly reach for, mapped to what we store. */
const AGENT_STATE_ALIASES: Record<string, AgentState> = {
  start: 'working',
  started: 'working',
  starting: 'working',
  running: 'working',
  in_progress: 'working',
  busy: 'working',
  waiting: 'waiting_on_you',
  waiting_for_user: 'waiting_on_you',
  waiting_on_user: 'waiting_on_you',
  needs_input: 'waiting_on_you',
  // These used to land on `waiting_on_you`, which was the closest thing there
  // was. Now that "come and look" is its own state they mean it, and a routine
  // reaching for the obvious word gets the right behaviour without having read
  // the protocol closely.
  needs_review: 'ready_for_review',
  review: 'ready_for_review',
  ready: 'ready_for_review',
  ready_for_review: 'ready_for_review',
  for_review: 'ready_for_review',
  awaiting_review: 'ready_for_review',
  waiting_for_review: 'ready_for_review',
  blocked: 'blocked',
  stuck: 'blocked',
  error: 'blocked',
  failed: 'blocked',
  finished: 'done',
  complete: 'done',
  completed: 'done',
  done: 'done',
  idle: 'idle',
};

export function coerceAgentState(value: unknown): AgentState | null {
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (isAgentState(key)) return key;
  return AGENT_STATE_ALIASES[key] ?? null;
}

/**
 * How long a routine may go quiet before "working" stops being believable.
 *
 * Twenty minutes was set from how often a routine is *asked* to report, which
 * is the wrong end: the question is not how often it should speak but how long
 * it can plausibly be busy without doing so. A single skill batch or test suite
 * clears twenty minutes routinely, and calling that death produced a queue full
 * of blocked tasks that were fine — which is worse than a slow catch, because a
 * queue nobody believes gets ignored wholesale.
 */
export const HEARTBEAT_STALE_MS = 45 * 60_000;

/**
 * The longest quiet window a routine may book for itself.
 *
 * There has to be a ceiling, because the window is the one thing standing
 * between a dead routine and a task that looks fine forever — a routine that
 * could ask for a day would have found a way to never be caught. Three hours
 * is longer than any single phase of work anybody has a reason to run without
 * speaking, and short enough that a session that died inside one is noticed
 * the same morning.
 */
export const QUIET_WINDOW_MAX_MINUTES = 180;

/**
 * Has this routine gone quiet in a way that means something is wrong?
 *
 * The plain heartbeat rule assumes silence is evidence: a routine that is
 * alive can always spare a POST. That holds right up until the work is one
 * long uninterruptible phase — a skill batch, a full test suite, a build — and
 * then it inverts, because the routine is not idle, it is busy in a way that
 * gives it no turn in which to speak. Scoring that as death is how a healthy
 * task ends up blocked while it is still working.
 *
 * So a routine can say in advance how long it is about to be unreachable, and
 * within that window silence is expected rather than damning. When the window
 * runs out the ordinary rule applies again — and applies immediately, since by
 * then the heartbeat is old. Booking quiet time is a promise to come back by a
 * stated moment, which is a stronger claim than saying nothing, not a weaker
 * one: break it and you are caught at the deadline you chose.
 */
export function isHeartbeatStale(
  task: Pick<Task, 'agent_state' | 'agent_heartbeat_at' | 'agent_quiet_until'>,
  now = Date.now(),
): boolean {
  if (task.agent_state !== 'working') return false;
  if (!task.agent_heartbeat_at) return false;
  if (task.agent_quiet_until && Date.parse(task.agent_quiet_until) > now) return false;
  return now - Date.parse(task.agent_heartbeat_at) > HEARTBEAT_STALE_MS;
}

/* ── who is listening on an adopted pull request ───────────────────────── */

/**
 * How an adopted pull request's worker was found, recorded when it is adopted.
 *
 *   - `session`: the pull request carries a Claude Code session link, and that
 *     session opened it — a comment on the pull request wakes it.
 *   - `nobody`:  no session link: a local terminal session, a person, a bot.
 *     Nothing Rigg posts on the pull request is heard by anyone it can name.
 *   - `routine`: handed to a routine by Rigg. That routine did not open the
 *     pull request, so it is not known that the pull request's comments wake
 *     it — Rigg reaches it by re-dispatch instead.
 */
export type TaskListener = 'session' | 'nobody' | 'routine';

export interface Listening {
  who: 'session' | 'reporting' | 'routine' | 'nobody';
  /** One line for the task, in plain words. */
  word: string;
}

/**
 * Whether Rigg can reach whoever is working on an adopted pull request.
 *
 * Null for a task Rigg dispatched: that routine is reached the way it always
 * has been, and saying so on every task would be noise. "The session has
 * ended" is the board's existing clock rather than a second one —
 * `isHeartbeatStale` once something has reported, and the same
 * `HEARTBEAT_STALE_MS` grace `needsYou` gives an import nobody claimed.
 */
export function listening(
  task: Pick<
    Task,
    'source' | 'listener' | 'agent_state' | 'agent_heartbeat_at' | 'agent_quiet_until' | 'created_at'
  >,
  now = Date.now(),
): Listening | null {
  if (task.source !== 'import') return null;
  if (task.listener === 'routine') {
    return { who: 'routine', word: 'Handed to a routine — Rigg reaches it by re-dispatch' };
  }
  const heard = Boolean(task.agent_heartbeat_at);
  const live = heard && !isHeartbeatStale(task, now);

  if (task.listener === 'nobody') {
    // A local session that claimed it and reports. Somebody is on it, but a
    // comment on the pull request is not how to reach them.
    if (live) return { who: 'reporting', word: 'A session reports on this, but pull-request comments may not reach it' };
    return { who: 'nobody', word: 'Nobody is on this — no session is listening on the pull request' };
  }

  // `session`, or an import from before this was recorded: assume the pull
  // request's own session until the clock says otherwise.
  const quiet = heard ? !live : now - Date.parse(task.created_at) > HEARTBEAT_STALE_MS;
  if (quiet) {
    return {
      who: 'nobody',
      word: heard
        ? 'Nobody is on this — its session has gone quiet'
        : 'Nobody is on this — its session never answered',
    };
  }
  return { who: 'session', word: 'Its session is listening on the pull request' };
}

/* ── design passes ──────────────────────────────────────────────────────── */

/**
 * What the routine that asked for a pass wants once it finishes.
 *
 * A design pass is something only a routine asks for — it is the party that has
 * read the code and knows whether the hard part of the task is the look. And a
 * routine cannot sit and wait for one: a pass runs for tens of minutes, and a
 * session held open that long is reclaimed. So asking comes with a choice of
 * what happens on the far side:
 *
 *   continue  the routine keeps working (or has already finished) and the pass
 *             runs alongside it. Nothing is dispatched when it lands; the merge
 *             is held until it does.
 *   resume    the routine ends its run, and Rigg dispatches one again when the
 *             pass is over — briefed with what the pass did, or why it failed.
 *
 * Every ordering the composer used to offer is one of these two, asked at the
 * moment the routine decides it — design first is `resume` asked before
 * building, a cleanup is `resume` asked after shooting the surface.
 */
export const DESIGN_THENS = ['continue', 'resume'] as const;
export type DesignThen = (typeof DESIGN_THENS)[number];

/**
 * How many passes one task may ask for.
 *
 * A routine asking is a program asking, and `resume` closes a loop with no
 * person in it: pass, dispatch, the routine asks again. Each turn is real time
 * and real money, so the loop has a ceiling. Three is a first pass, a second to
 * continue a batch that ran out of clock, and one more — past that, what the
 * task needs is a person, and the refusal says so.
 */
export const DESIGN_PASSES_PER_TASK = 3;

/**
 * Is the routine waiting for a pass to hand the task back?
 *
 * True between a routine asking with `resume` and the pass finishing. Nobody is
 * working the task in that window — the routine has ended its run on purpose —
 * and nobody needs to look at it yet either, because a routine is coming back.
 */
export function isWaitingOnDesign(task: Pick<Task, 'design_state' | 'design_then'>): boolean {
  return designPassOutstanding(task) && task.design_then === 'resume';
}

export const DESIGN_STATES = ['requested', 'running', 'done', 'error'] as const;
export type DesignState = (typeof DESIGN_STATES)[number];

export function isDesignState(value: unknown): value is DesignState {
  return typeof value === 'string' && (DESIGN_STATES as readonly string[]).includes(value);
}

/**
 * How long a design worker may go quiet before it counts as down.
 *
 * Much tighter than a routine's heartbeat, and for the opposite reason: this is
 * not a guess about how long work might take, it is the age of a *poll*. A
 * worker that is up asks on a fixed timer whether it has anything to do, so
 * silence here is not "busy", it is "not running". Three missed polls at the
 * documented 30s interval, with room for a slow one.
 */
export const DESIGN_WORKER_OFFLINE_MS = 2 * 60_000;

/**
 * Is this worker down?
 *
 * Only ever asked of a pull worker: a push routine has no reason to call in
 * between dispatches, so the age of `last_seen_at` says nothing about it and
 * scoring it as offline would be a red light on a healthy routine. Never having
 * called in at all *is* down — a worker that has never polled has never run.
 */
export function isWorkerOffline(
  agent: Pick<Agent, 'kind' | 'last_seen_at'>,
  now = Date.now(),
): boolean {
  if (agent.kind !== 'design') return false;
  if (!agent.last_seen_at) return true;
  return now - Date.parse(agent.last_seen_at) > DESIGN_WORKER_OFFLINE_MS;
}

/**
 * Has the design pass gone quiet in a way that means something is wrong?
 *
 * The same rule as `isHeartbeatStale` and for the same reason, asked of the
 * other worker on the task. It has to be a separate question because the two
 * are independent: a routine that finished this morning is not evidence about
 * a pass running now, and a pass reporting every two minutes is not evidence
 * that the routine is alive.
 *
 * Only ever true while a pass is actually running. A pass that has finished is
 * not quiet, it is over.
 *
 * Distinct from `sweep_stale_design_passes`, which asks whether the *worker*
 * is still there and ends the pass when it is not. This asks whether the pass
 * itself is still talking, on a worker that is demonstrably alive — #421's
 * exact shape, where the Mac mini polled happily throughout and the pass said
 * nothing for forty-four minutes. Nothing is ended on the strength of it: it
 * is a thing to show somebody, because the alternative to showing it is a
 * violet box that says "running" for an hour and means nothing.
 */
export function isDesignPassStalled(
  task: Pick<Task, 'design_state' | 'design_activity_at' | 'design_quiet_until' | 'design_claimed_at'>,
  now = Date.now(),
): boolean {
  if (task.design_state !== 'running') return false;
  if (task.design_quiet_until && Date.parse(task.design_quiet_until) > now) return false;
  // Claimed but never heard from is measured from the claim, so a worker that
  // dies between claiming and starting is caught by the same rule rather than
  // being quietly exempt from it.
  const last = task.design_activity_at ?? task.design_claimed_at;
  if (!last) return false;
  return now - Date.parse(last) > HEARTBEAT_STALE_MS;
}

/**
 * What the worker's code is, relative to what it should be.
 *
 * A design worker is the one part of Rigg that Rigg does not deploy — it is a
 * process somebody started on a Mac mini — so "did the machine ever pick the
 * fix up" is otherwise unanswerable from the board. The worker reports this
 * because it is the only party that can: it fetches its own remote and counts.
 *
 *   current   running the tip of its own branch
 *   behind    an update is waiting, and it will take it at the next idle poll
 *   modified  the checkout has local changes, so it will not pull over them and
 *             the sha it reports does not fully describe what is running.
 *             Somebody is working on that machine — a reason, not a fault, but
 *             it is why the version is going to stay where it is.
 *   unknown   it has not polled since this shipped, or it is a push routine
 *
 * `modified` outranks `behind` because it is the more actionable of the two: a
 * worker that is behind fixes itself in sixty seconds, and one with local
 * changes never will until a person goes and looks at the machine.
 */
export type WorkerVersionState = 'current' | 'behind' | 'modified' | 'unknown';

export function workerVersionState(
  agent: Pick<Agent, 'kind' | 'worker_behind' | 'worker_dirty'>,
): WorkerVersionState {
  if (agent.kind !== 'design' || agent.worker_behind === null) return 'unknown';
  if (agent.worker_dirty) return 'modified';
  return agent.worker_behind === 0 ? 'current' : 'behind';
}

/**
 * Is a design pass owed on this task right now?
 *
 * Load-bearing in two places that must agree: the merge driver holds a pull
 * request while this is true, and a routine asking for a second pass over one
 * already in flight is refused. Both are the same question, so both ask it here.
 */
export function designPassOutstanding(
  task: Pick<Task, 'design_state'>,
): boolean {
  return task.design_state === 'requested' || task.design_state === 'running';
}

/* ── status note ────────────────────────────────────────────────────────── */

/* ── how long a prompt may be ───────────────────────────────────────────── */

/**
 * Claude Code's routine trigger rejects the whole request above 65,536
 * characters of `text`. Cut well short of the hard limit rather than at it: a
 * long task description shouldn't be able to 400 the dispatch, and the margin
 * costs nothing on a brief that is normally a few thousand characters.
 * `brief.ts` fits every dispatch to this, saying so where it truncates.
 */
export const FIRE_TEXT_LIMIT = 60_000;

/**
 * A project's own standing instructions ride in EVERY brief that project
 * dispatches, so they are the one part of the overhead that varies.
 */
export const PROJECT_INSTRUCTIONS_MAX_LENGTH = 20_000;

/**
 * How long a task's `prompt` may be — **derived, not picked**.
 *
 * A prompt over this doesn't fail loudly; it fails quietly, by pushing the
 * assembled brief past `FIRE_TEXT_LIMIT` so `fitFireText` cuts the tail off.
 * The number is therefore the fire limit minus everything else a brief
 * carries, each at its own maximum: the project's instructions, plus the
 * protocol pointer and task header. Those two come to about 5,000 characters
 * on a plain task and about 5,800 at their worst — auto-merge on, sent back, a
 * 200-character title, long project and repo names, real callback URLs — so
 * the allowance below leaves roughly 2,000 characters of margin for the
 * pointer to grow into. `test/protocol.test.ts` dispatches that worst case and
 * fails once the margin drops under 1,000.
 *
 * It was 20,000, which was a guess and too small: a caller with a genuinely
 * long brief — a CI self-heal naming a 250-commit range, say — was refused
 * outright at the door with nothing to do about it.
 */
const BRIEF_OVERHEAD_ALLOWANCE = 8_000;
export const TASK_PROMPT_MAX_LENGTH =
  FIRE_TEXT_LIMIT - PROJECT_INSTRUCTIONS_MAX_LENGTH - BRIEF_OVERHEAD_ALLOWANCE;

/** The comment an admin can leave when handing a pull request to a routine — it leads the brief. */
export const HAND_OFF_NOTE_MAX_LENGTH = 4_000;

/**
 * `status_note` is the field a person reads first — it stops being useful the
 * moment it is long enough that nobody actually reads it. Kept here, rather
 * than only as a `.max()` on the API schema, because a bare zod message can't
 * say how far over a routine is, and that is the one detail that actually
 * helps it fix the report.
 */
export const STATUS_NOTE_MAX_LENGTH = 500;

/** `null` when the note is fine; otherwise the message to hand back. */
export function statusNoteError(note: string | null | undefined): string | null {
  if (!note || note.length <= STATUS_NOTE_MAX_LENGTH) return null;
  return (
    `status_note is ${note.length} characters — the limit is ${STATUS_NOTE_MAX_LENGTH}. ` +
    `It's the field a person reads first, so trim it to where things stand right now, ` +
    `not a log of everything you did.`
  );
}

/* ── measured risk ──────────────────────────────────────────────────────── */

/**
 * What a routine measured about its own change, once the work is done.
 *
 * Reported rather than inferred, like `reviewed_sha`: only the routine knows
 * which issue it fixed and what it touched. Stored whole and replaced whole —
 * a later report is a later measurement, and the post-work one beats the
 * pre-work estimate. Validated strictly at the door (the agent callback's
 * `riskSchema`), so anything on a task has exactly this shape.
 *
 *   1 contained   docs, copy, a one-line fix away from anything sensitive
 *   2 ordinary    a normal change a person should look at
 *   3 sensitive   migrations, auth, sends, infra, CI, agent prompts, …
 */
export const RISK_LEVELS = [1, 2, 3] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];

export const RISK_LEVEL_WORDS: Record<RiskLevel, string> = {
  1: 'contained',
  2: 'ordinary',
  3: 'sensitive',
};

export const UX_IMPACTS = ['none', 'cosmetic', 'minimal', 'moderate', 'major'] as const;
export type UxImpact = (typeof UX_IMPACTS)[number];

export interface TaskRisk {
  level: RiskLevel;
  ux_impact: UxImpact;
  lines_changed: number;
  issue_ref: number | null;
  issue_gist: string | null;
  fix_gist: string | null;
}

/** Longest `issue_gist` / `fix_gist` accepted — a line, not a paragraph. */
export const RISK_GIST_MAX_LENGTH = 300;

/* ── scheduled tasks ────────────────────────────────────────────────────── */

/** A task Rigg opens on a timer — see the `waiver_schedules_risk` migration. */
export interface TaskSchedule {
  id: string;
  project_id: string;
  /** Five fields, evaluated by pg_cron in UTC. */
  cron: string;
  title: string;
  prompt: string;
  category: string | null;
  kind: TaskKind | null;
  auto_merge: boolean;
  smart: boolean;
  /** The routine every firing goes to; null is the rotation. */
  agent_id: string | null;
  /**
   * Every firing goes straight to `agent_id` with no callback, and the task
   * kept for the record is `background` — off the board. Needs `agent_id`;
   * never with `auto_merge`.
   */
  fire_and_forget: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  last_fired_at: string | null;
  last_task_id: string | null;
  last_error: string | null;
}

/**
 * Whether a schedule's previous task is still open and waiting on a person, so
 * the next firing belongs on it rather than in a task of its own.
 *
 * Open: not landed, failed, withdrawn or in the trash — a person who threw it
 * away has answered it. Snoozed still counts: it was put aside, not answered,
 * and the firing would only bring the same question back.
 */
export function scheduleTaskParked(
  task: Pick<Task, 'status' | 'agent_state' | 'archived_at' | 'deleted_at'>,
): boolean {
  if (task.status === 'merged' || task.status === 'deployed' || task.status === 'error') return false;
  if (task.archived_at || task.deleted_at) return false;
  return task.agent_state === 'blocked' || task.agent_state === 'waiting_on_you';
}

/** Inclusive bounds for each of the five cron fields, in order. */
const CRON_FIELDS: { name: string; min: number; max: number }[] = [
  { name: 'minute', min: 0, max: 59 },
  { name: 'hour', min: 0, max: 23 },
  { name: 'day of month', min: 1, max: 31 },
  { name: 'month', min: 1, max: 12 },
  { name: 'day of week', min: 0, max: 7 },
];

/**
 * `null` when a cron expression is five well-formed fields; otherwise what is
 * wrong with it.
 *
 * pg_cron parses it again when the schedule is saved and is the final word —
 * this exists so the common mistakes come back as a sentence naming the field
 * rather than as a database error. Numbers, `*`, lists, ranges and `/` steps;
 * no names (`MON`) and no `@weekly`, which keeps what is stored readable to
 * whoever opens Settings next.
 */
export function cronExpressionError(expression: string): string | null {
  const fields = expression.trim().split(/\s+/);
  if (fields.length !== 5) {
    return `a cron expression has five fields (minute hour day-of-month month day-of-week) — this has ${fields.length}`;
  }
  for (let i = 0; i < 5; i += 1) {
    const { name, min, max } = CRON_FIELDS[i]!;
    for (const part of fields[i]!.split(',')) {
      const match = /^(\*|(\d+)(?:-(\d+))?)(?:\/(\d+))?$/.exec(part);
      if (!match) return `the ${name} field "${fields[i]}" is not a number, range, list or step`;
      const [, , from, to, step] = match;
      for (const value of [from, to]) {
        if (value !== undefined && (Number(value) < min || Number(value) > max)) {
          return `the ${name} field allows ${min}–${max}, not ${value}`;
        }
      }
      if (from !== undefined && to !== undefined && Number(from) > Number(to)) {
        return `the ${name} range ${from}-${to} runs backwards`;
      }
      if (step !== undefined && Number(step) === 0) return `the ${name} step cannot be 0`;
    }
  }
  return null;
}

/* ── link labels ────────────────────────────────────────────────────────── */

/**
 * A label sits on a button, not under a paragraph — "Preview desktop" is the
 * ruler. Kept here rather than only as a `.max()` on the API schema for the
 * same reason as `STATUS_NOTE_MAX_LENGTH`: a bare zod message can't say how
 * far over a routine is, and a routine cannot fix what it isn't told.
 */
export const LINK_LABEL_MAX_LENGTH = 'Preview desktop'.length;

/** `null` when the label is fine; otherwise the message to hand back. */
export function linkLabelError(label: string | null | undefined): string | null {
  if (!label || label.length <= LINK_LABEL_MAX_LENGTH) return null;
  return (
    `link label is ${label.length} characters — the limit is ${LINK_LABEL_MAX_LENGTH}, about as ` +
    `long as "Preview desktop". It sits on a small button, so say what it is ` +
    `("Mobile preview", "Storybook"), not why — trim it and resend.`
  );
}

/* ── CI ─────────────────────────────────────────────────────────────────── */

export const CI_STATES = ['none', 'queued', 'running', 'passing', 'failing'] as const;
export type CiState = (typeof CI_STATES)[number];

/** Conclusions that mean the check genuinely did not pass. */
export const FAILED_CONCLUSIONS = new Set([
  'failure',
  'timed_out',
  'cancelled',
  'action_required',
  'startup_failure',
]);

/** Conclusions that are not failures and must not hold a branch back. */
export const IGNORED_CONCLUSIONS = new Set(['neutral', 'skipped', 'stale']);

export interface CheckLike {
  status: string; // queued | in_progress | completed
  conclusion?: string | null;
}

/**
 * The same arithmetic the `rollup_ci` SQL function performs, kept here so the
 * UI and the PAT-fallback poller agree with the trigger. Failing beats running
 * beats queued beats passing.
 */
export function rollupChecks(runs: CheckLike[]): { state: CiState; summary: string | null } {
  let failing = 0;
  let running = 0;
  let queued = 0;
  let passed = 0;

  for (const run of runs) {
    if (run.status === 'queued') queued += 1;
    else if (run.status !== 'completed') running += 1;
    else if (FAILED_CONCLUSIONS.has(run.conclusion ?? '')) failing += 1;
    else if (run.conclusion && !IGNORED_CONCLUSIONS.has(run.conclusion)) passed += 1;
  }

  if (failing > 0) return { state: 'failing', summary: `${failing} failing` };
  if (running > 0) return { state: 'running', summary: `${running} running` };
  if (queued > 0) return { state: 'queued', summary: `${queued} queued` };
  if (passed > 0) return { state: 'passing', summary: `${passed} passed` };
  return { state: 'none', summary: null };
}

/* ── people and permissions ─────────────────────────────────────────────── */

export const ROLES = ['admin', 'member'] as const;
export type Role = (typeof ROLES)[number];

/**
 * A member can sign a task off — "looks right to me" — which parks it in the
 * admin's queue. Only an admin approves the merge or marks work merged or
 * deployed. Either may send work back.
 */
const MEMBER_MAY_SET: readonly TaskStatus[] = ['signed_off', 'working'];

export function canRoleSet(role: Role, to: TaskStatus): boolean {
  return role === 'admin' || MEMBER_MAY_SET.includes(to);
}

export class PermissionError extends Error {
  readonly status = 403;
  constructor(message: string) {
    super(message);
    this.name = 'PermissionError';
  }
}

export function assertRoleMaySet(role: Role, to: TaskStatus): void {
  if (canRoleSet(role, to)) return;
  throw new PermissionError(
    to === 'approved'
      ? 'only an admin can approve a merge — sign it off and an admin will confirm'
      : `only an admin can mark a task ${to}`,
  );
}

/**
 * What an admin's Approve means for this task, right now.
 *
 * Approval used to mean one thing: `status = approved`, and a comment on the
 * pull request telling the routine to "land it when the checks are green". That
 * reads well for work that has been handed over. For work still in progress it
 * is an instruction to merge half a change — the routine wakes on the comment,
 * sees `approved: true`, and the most natural reading of both is "merge now".
 *
 * So an approval given mid-stream is recorded as a standing one instead:
 * `auto_merge` goes on, `approved_at` is stamped, and the task stays in
 * `working`. The routine finishes the work, reports `reviewed_sha`, and the
 * merge driver lands it once it is clean, green and reviewed at the head —
 * exactly the path a task raised with auto-merge takes. Nothing merges on the
 * approval itself.
 *
 * "Mid-stream" is the ladder saying the work is not handed over yet (`fresh`
 * or `working`) and the routine not saying otherwise: a routine that reported
 * `ready_for_review`, `waiting_on_you` or `done` has put the work in front of a
 * person, and approving that is the ordinary approval it always was.
 */
export type ApprovalMode = 'approve' | 'auto_merge';

const HANDED_OVER_STATES: ReadonlySet<AgentState> = new Set(['ready_for_review', 'waiting_on_you', 'done']);

export function approvalMode(task: Pick<Task, 'status' | 'agent_state'>): ApprovalMode {
  if (task.status !== 'fresh' && task.status !== 'working') return 'approve';
  if (HANDED_OVER_STATES.has(task.agent_state)) return 'approve';
  return 'auto_merge';
}

export type Actor = 'user' | 'agent' | 'system' | 'github';

/* ── links and images ───────────────────────────────────────────────────── */

/**
 * Links are labelled because there is rarely one of anything: a desktop
 * preview, a mobile preview and a Storybook are all `preview`, and "Preview"
 * three times tells a reviewer nothing.
 */
export const LINK_KINDS = ['preview', 'session', 'deploy', 'pr', 'other'] as const;
export type LinkKind = (typeof LINK_KINDS)[number];

export interface TaskLink {
  kind: LinkKind;
  label: string;
  url: string;
}

export interface TaskImage {
  url: string;
  label: string | null;
}

const DEFAULT_LABEL: Record<LinkKind, string> = {
  preview: 'Preview',
  session: 'Session',
  deploy: 'Live',
  pr: 'Pull request',
  other: 'Link',
};

export interface ReportedLink {
  url: string;
  label?: string | null;
  kind?: string | null;
}

export interface ReportedImage {
  url: string;
  label?: string | null;
}

/**
 * Only http(s) survives. A routine reports these and a browser renders them
 * straight into the page, so `javascript:` and `data:` have no business here.
 */
export function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

/** Trim, drop blanks and anything non-http, default kind and label, de-dupe. */
export function normaliseLinks(reported: ReportedLink[]): TaskLink[] {
  const seen = new Set<string>();
  const out: TaskLink[] = [];
  for (const item of reported) {
    const url = item.url?.trim();
    if (!url || seen.has(url) || !isSafeUrl(url)) continue;
    seen.add(url);
    const kind = (LINK_KINDS as readonly string[]).includes(item.kind ?? '')
      ? (item.kind as LinkKind)
      : 'other';
    out.push({ kind, label: item.label?.trim().slice(0, LINK_LABEL_MAX_LENGTH) || DEFAULT_LABEL[kind], url });
  }
  return out;
}

export function normaliseImages(reported: ReportedImage[]): TaskImage[] {
  const seen = new Set<string>();
  const out: TaskImage[] = [];
  for (const item of reported) {
    const url = item.url?.trim();
    if (!url || seen.has(url) || !isSafeUrl(url)) continue;
    seen.add(url);
    out.push({ url, label: item.label?.trim().slice(0, 120) || null });
  }
  return out;
}

/**
 * How many screenshots one task can hold.
 *
 * Kept here rather than only as a `.max()` on the schema for the same reason
 * as `STATUS_NOTE_MAX_LENGTH`: a bare zod message cannot say how far over a
 * routine is, and on #421 the silent version of this cost sixty-eight frames.
 * The routine shot eighty-eight, the cap was twenty, and nothing told it that
 * sixty-eight had been dropped — so the design pass that read them designed
 * against a quarter of the surface, believing it had the lot.
 */
export const IMAGES_MAX_COUNT = 200;

export class SessionLinkRequiredError extends Error {
  readonly status = 400;
  constructor(message: string) {
    super(message);
    this.name = 'SessionLinkRequiredError';
  }
}

export class ImagesOverflowError extends Error {
  readonly status = 400;
  constructor(
    readonly total: number,
    readonly existing: number,
  ) {
    super(
      `that would put ${total} screenshots on this task — the limit is ${IMAGES_MAX_COUNT}, ` +
        `and ${existing} ${existing === 1 ? 'is' : 'are'} already there. ` +
        `Send fewer, or replace the set with \`images\` instead of adding to it with \`add_images\`.`,
    );
    this.name = 'ImagesOverflowError';
  }
}

/**
 * Add screenshots to the ones a task already has.
 *
 * The counterpart to reporting `images`, which replaces. Both are needed and
 * for different moments: a routine that shoots a surface in one go knows the
 * whole set and should send it, while one that finds three more states worth
 * capturing an hour later would have to re-send all eighty-eight to keep them,
 * and re-sending a set it no longer holds in context is exactly how a set gets
 * quietly truncated.
 *
 * Existing first, in the order they were added, because they are the ones a
 * person has already been looking at and a stable position is what makes a
 * thumbnail grid usable. Already-present URLs are dropped rather than
 * duplicated, so re-sending an overlapping batch is safe — the same promise
 * every other field in a report makes.
 */
export function appendImages(existing: TaskImage[], added: ReportedImage[]): TaskImage[] {
  const seen = new Set(existing.map((image) => image.url));
  return [...existing, ...normaliseImages(added).filter((image) => !seen.has(image.url))];
}

/** Whether there is actually something to look at. */
export function isReviewable(task: Pick<Task, 'links'>): boolean {
  return (task.links ?? []).some((link) => link.kind === 'preview');
}

export function previewLinks(task: Pick<Task, 'links'>): TaskLink[] {
  return (task.links ?? []).filter((link) => link.kind === 'preview');
}

/* ── categories ─────────────────────────────────────────────────────────── */

/** "User feedback" and "user-feedback" are the same category. */
export function slugifyCategory(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/** Title-case a slug back into something readable, for agent-invented ones. */
export function labelForCategory(input: string): string {
  const cleaned = input.trim().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');
  if (!cleaned) return 'Uncategorised';
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/* ── records ────────────────────────────────────────────────────────────── */

/**
 * `'background'` is a fire-and-forget intake's task: opened for the record,
 * never carrying a callback token, and kept off the board by default — see
 * `inQueue` in the web app's `selectors.ts`.
 */
export type TaskSource = 'human' | 'agent' | 'import' | 'background';

/** What kind of work a task is. Null on a task nobody classified. */
export const TASK_KINDS = ['bug', 'enhancement', 'feature', 'chore'] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

export const KIND_LABELS: Record<TaskKind, string> = {
  bug: 'Bug',
  enhancement: 'Enhancement',
  feature: 'Feature',
  chore: 'Chore',
};

/**
 * Which door a task came through — finer than `source`, which stays what the
 * filters and the `background` logic read. Stamped by `createTask`'s caller,
 * never inferred afterwards.
 */
export const TASK_ORIGINS = [
  'person',
  'api_key',
  'brigg_issue',
  'schedule',
  'intake',
  'follow_up',
  'routine',
  'import',
] as const;
export type TaskOrigin = (typeof TASK_ORIGINS)[number];

/** The filter's and the breakdown's name for each origin. */
export const ORIGIN_LABELS: Record<TaskOrigin, string> = {
  person: 'Person',
  api_key: 'API key',
  brigg_issue: 'Brigg issue',
  schedule: 'Schedule',
  intake: 'Intake',
  follow_up: 'Follow-up',
  routine: 'Routine',
  import: 'Adopted PR',
};

/** Longest `origin_ref` a caller may assert — a handle, not a sentence. */
export const ORIGIN_REF_MAX_LENGTH = 80;
/** Longest `quote` accepted — what a customer said, not a transcript. */
export const QUOTE_MAX_LENGTH = 4_000;

/**
 * Where a task came from, as one phrase that reads naturally: "from Kyrre",
 * "Brigg #412", "schedule: Nightly sweep". `who` is the person behind a
 * `person` task, or the routine behind a `routine` one, when the caller knows
 * a name `origin_ref` does not carry.
 */
export function describeOrigin(
  task: Pick<Task, 'origin' | 'origin_ref'>,
  who?: string | null,
): string {
  const ref = task.origin_ref;
  switch (task.origin) {
    case 'person':
      return who ? `from ${who}` : 'from a person';
    case 'api_key':
      return who ? `via API key (${who})` : 'via API key';
    case 'brigg_issue':
      return ref ? `Brigg ${ref}` : 'Brigg issue';
    case 'schedule':
      return ref ? `schedule: ${ref}` : 'schedule';
    case 'intake':
      return ref ? `intake: ${ref}` : 'intake';
    case 'follow_up':
      return ref ? `follow-up of ${ref}` : 'follow-up';
    case 'routine':
      return `by ${ref ?? who ?? 'a routine'}`;
    case 'import':
      return 'adopted PR';
  }
}

export interface Task {
  id: string;
  ref: number;
  project_id: string;
  repo_id: string | null;
  category_id: string | null;
  title: string;
  prompt: string;
  status: TaskStatus;
  source: TaskSource;
  kind: TaskKind | null;
  origin: TaskOrigin;
  /** The origin's handle: `#412`, a schedule's title, an intake's name. */
  origin_ref: string | null;
  /**
   * The requester's exact words. Display-only, for board members: never in
   * the prompt, the brief, or any `/agent/*` response.
   */
  quote: string | null;
  created_by: string | null;
  agent_id: string | null;
  auto_merge: boolean;
  /** Asks auto-pick for a `smart` agent instead of a `normal` one. */
  smart: boolean;
  branch: string | null;
  pr_url: string | null;
  pr_number: number | null;
  pr_state: string | null;
  pr_draft: boolean | null;
  /** What the primary pull request calls itself. Null until GitHub has said. */
  pr_title?: string | null;
  /** Head sha the routine's mandatory review covered. The merge gate. */
  reviewed_sha: string | null;
  /** Head sha Rigg last nudged about — one nudge per state of the branch. */
  merge_nudged_sha: string | null;
  /** When that nudge went out; with the count, drives the widening repeat. */
  merge_nudged_at: string | null;
  /** Consecutive nudges at the current head. Reset when the head moves. */
  merge_nudge_count: number;
  status_note: string | null;
  /** When `status_note` last actually changed value. Null if it never has. */
  status_note_at: string | null;
  last_message: string | null;
  agent_state: AgentState;
  answered_at: string | null;
  agent_activity: string | null;
  agent_activity_at: string | null;
  agent_heartbeat_at: string | null;
  agent_quiet_until: string | null;
  claim_code_hash: string | null;
  claim_expires_at: string | null;
  claimed_at: string | null;
  /**
   * Who can hear this task's pull-request comments — only set on an adopted
   * pull request. See `listening`.
   */
  listener?: TaskListener | null;
  ci_state: CiState;
  ci_summary: string | null;
  ci_url: string | null;
  ci_updated_at: string | null;
  links: TaskLink[];
  images: TaskImage[];
  signed_off_by: string | null;
  signed_off_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  dispatch_attempts: number;
  last_error: string | null;
  /**
   * Set while the task is waiting for a routine — nobody could take it, every
   * routine was out of quota, or the one it was sent to refused. The queue
   * sweep retries it; see `dispatchOrQueue`. Null once a routine accepts it.
   */
  queued_at: string | null;
  /** Why it is waiting, in a sentence. */
  queue_reason: string | null;
  /** When the wait is expected to end, if anybody knows — a quota reset. */
  queue_until: string | null;
  /** Not to be tried again before this — the backoff after a refusal. */
  queue_retry_at: string | null;
  /** Refusals in a row while queued. Resets when a routine accepts it. */
  queue_failures: number;
  /** A routine it was explicitly sent to, kept so the retry goes there too. */
  queue_agent_id: string | null;
  created_at: string;
  updated_at: string;
  dispatched_at: string | null;
  first_preview_at: string | null;
  /** First preview link *or* screenshot — see the comment in report.ts. */
  first_reviewable_at: string | null;
  reopened_at: string | null;
  merged_at: string | null;
  deployed_at: string | null;
  /** Set when the pull request closed without merging; null otherwise. */
  archived_at: string | null;
  /** Set when a person moves the task to the trash; cleared on restore. */
  deleted_at: string | null;
  /** Set when a person snoozes the task to park it; cleared when it wakes. */
  snoozed_at: string | null;
  /** SUPERSEDED — a routine asks for passes itself; no longer read. */
  design_pass: boolean;
  /** The pass happening now, if any. NULL = none outstanding or ever run. */
  design_state: DesignState | null;
  /** What the routine asked the pass to do. */
  design_message: string | null;
  /** What the routine wants once the pass is over. */
  design_then: DesignThen | null;
  design_requested_at: string | null;
  design_agent_id: string | null;
  design_claimed_at: string | null;
  design_finished_at: string | null;
  design_error: string | null;
  /** Design passes asked for on this task, ever. >1 means continue, not restart. */
  design_pass_count: number;
  /** What the design pass is doing — its own line, not the routine's. */
  design_activity: string | null;
  /** When the design pass last said anything. Its heartbeat. */
  design_activity_at: string | null;
  /** The design pass's "where this stands", kept apart from the routine's. */
  design_note: string | null;
  design_note_at: string | null;
  /** Silence from the design pass is expected until this moment. */
  design_quiet_until: string | null;
  /** The latest risk block the routine reported, or null if it never sent one. */
  risk: TaskRisk | null;
  /**
   * The check that stood in for approval when Rigg merged this, if one did —
   * see `merge_waiver_check`. Null on anything a person (or auto-merge) approved.
   */
  merge_waived_by: string | null;
  /**
   * Why the merge driver is holding an auto-merge task for a person: its pull
   * request touches one of the repository's protected paths (see
   * `Repo.protected_paths`). Null when nothing holds it.
   */
  merge_held_by: string | null;
}

/** A pull request that was withdrawn rather than landed. */
export function isArchived(task: Pick<Task, 'archived_at'>): boolean {
  return Boolean(task.archived_at);
}

/** In the trash — deleted by a person, and recoverable until it is restored. */
export function isTrashed(task: Pick<Task, 'deleted_at'>): boolean {
  return Boolean(task.deleted_at);
}

/** Parked by a person; out of the working queues until it wakes. */
export function isSnoozed(task: Pick<Task, 'snoozed_at'>): boolean {
  return Boolean(task.snoozed_at);
}

/* ── multiple pull requests ─────────────────────────────────────────────── */

export const TASK_PR_STATES = ['open', 'draft', 'closed', 'merged'] as const;
export type TaskPrState = (typeof TASK_PR_STATES)[number];

/**
 * One pull request associated with a task — the primary one (mirrored on
 * `Task.pr_url` and friends, for everything that only ever needed one)
 * included. See the `task_prs` migration for why this exists rather than
 * only the single columns.
 */
export interface TaskPr {
  id: string;
  task_id: string;
  project_id: string;
  repo_id: string | null;
  number: number;
  url: string;
  state: TaskPrState;
  draft: boolean;
  head_sha: string | null;
  base_branch: string | null;
  /** The pull request's own title, once GitHub has said. */
  title?: string | null;
  merged_at: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Whether a task's pull requests have landed: at least one merged, and none
 * still open.
 *
 * `false` on an empty list, deliberately: "no pull requests" is not "landed",
 * and a task that has never opened one must not read as done just because
 * there is nothing in the list to disagree.
 *
 * A pull request closed without merging does not hold the task back. It used
 * to: "every one merged" left a task whose first attempt was closed in favour
 * of a second sitting in Merging for good, approved and landed and waiting on
 * a pull request nobody was ever going to merge. The closed one is withdrawn
 * work; the merged one is the work.
 */
export function prsLanded(prs: readonly Pick<TaskPr, 'state'>[]): boolean {
  return (
    prs.some((pr) => pr.state === 'merged') &&
    prs.every((pr) => pr.state === 'merged' || pr.state === 'closed')
  );
}

/**
 * Whether every pull request attached to a task ended without landing.
 *
 * The other side of `prsLanded` — also `false` on an empty list — and mixed
 * outcomes (one merged, one closed) count as neither: the task is not done,
 * because the merged half is real work that landed, so it must not read as
 * withdrawn.
 */
export function allPrsClosedWithoutMerge(prs: readonly Pick<TaskPr, 'state'>[]): boolean {
  return prs.length > 0 && prs.every((pr) => pr.state === 'closed');
}

export interface Project {
  id: string;
  name: string;
  slug: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Repo {
  id: string;
  project_id: string;
  owner: string;
  name: string;
  default_branch: string;
  installation_id: number | null;
  private: boolean;
  /**
   * Path globs a task's `auto_merge` never waives approval for. A pull request
   * touching one waits for a person, flag or no flag. Empty is no protection,
   * which is the default — see `protectedPathHit` in `merge.ts`.
   */
  protected_paths: string[];
  created_at: string;
  updated_at: string;
}

/**
 * Which routine service an endpoint belongs to.
 *
 * The operator picks this; Rigg supplies the protocol. The alternative — a
 * free-form header map on each routine — makes every instance retype one
 * vendor's constants and go stale when that vendor changes them.
 */
export type AgentProvider = 'anthropic';

export const AGENT_PROVIDERS: readonly AgentProvider[] = ['anthropic'] as const;

/**
 * Which direction work travels.
 *
 * `routine` is the original: Rigg holds an address and POSTs to it. `design` is
 * a worker with no address at all — it polls, claims, and reports back over the
 * same callback API. That is not a downgrade. A machine on somebody's desk has
 * no stable inbound route without a tunnel, and asking removes the tunnel, the
 * dispatch timeout and the whole class of "we sent it and heard nothing".
 *
 * It also means a design worker is never *picked*: the round-robin is for work
 * Rigg pushes, and a pass is always aimed at one task by a person or by the
 * task's own toggle.
 */
export type AgentKind = 'routine' | 'design';

export const AGENT_KINDS: readonly AgentKind[] = ['routine', 'design'] as const;

export function isAgentKind(value: unknown): value is AgentKind {
  return typeof value === 'string' && (AGENT_KINDS as readonly string[]).includes(value);
}

/**
 * How capable an agent is taken to be, for routing rather than for anything
 * Rigg itself measures.
 *
 * `normal` is the default and is who ordinary, untagged work goes to.
 * `smart` is not "also gets normal work" — `nextAgent` matches a task's level
 * to an agent's exactly, so a `smart` agent sits out of the everyday rotation
 * the same way a paused one would, but without being paused: it wakes up for
 * a task tagged `smart` instead of needing to be resumed by hand. See the
 * 20260918120000_agent_intelligence migration.
 */
export type AgentIntelligence = 'normal' | 'smart';

export const AGENT_INTELLIGENCES: readonly AgentIntelligence[] = ['normal', 'smart'] as const;

export function isAgentIntelligence(value: unknown): value is AgentIntelligence {
  return typeof value === 'string' && (AGENT_INTELLIGENCES as readonly string[]).includes(value);
}

/* ── the dispatch queue ─────────────────────────────────────────────────── */

/**
 * Refusals in a row before the queue stops retrying and puts the task in
 * front of a person. With the backoff below that is about three hours of
 * trying — long enough to ride out an outage or a daily run cap, short enough
 * that a routine with a revoked token does not hide a task all day.
 */
export const QUEUE_MAX_FAILURES = 10;

/** 1, 2, 4, 8, 16, then every 30 minutes. */
export function queueBackoffMs(failures: number): number {
  const minutes = Math.min(30, 2 ** Math.max(0, failures - 1));
  return minutes * 60 * 1000;
}

/* ── usage: how much of its subscription a routine has left ─────────────── */

/**
 * What a Claude subscription says about one of its rate-limit windows.
 *
 * Rigg asks the subscription itself, hourly, with the key set on it — see
 * `usageFromRateLimitHeaders`. A routine that can see its own session record
 * may report `rate_limit_info` too. Either way these are the three values:
 * `allowed`, `allowed_warning` (about to run out) and `rejected` (out).
 */
export type UsageStatus = 'allowed' | 'allowed_warning' | 'rejected';

export const USAGE_STATUSES: readonly UsageStatus[] = ['allowed', 'allowed_warning', 'rejected'] as const;

/** One window of one subscription, as last reported. */
export interface UsageWindow {
  /** `five_hour`, `seven_day`, `seven_day_opus` … — free text, as reported. */
  window: string;
  status: UsageStatus;
  /** When this window rolls over. After that the reading says nothing. */
  resets_at: string | null;
  /** 0–1. Only when the routine could see a figure — the session record has none. */
  utilization: number | null;
  /** Running on paid extra usage: allowed, but no longer free. */
  overage: boolean | null;
  reported_at: string;
  /** Which routine sent it — on a shared subscription, not always this one. */
  reported_by?: string | null;
}

/** Keyed by `window`: a later reading of one window replaces the earlier. */
export type AgentUsage = Record<string, UsageWindow>;

/**
 * How long a reading with no reset time is believed. The shortest window a
 * subscription has is five hours, so nothing outlives it without being renewed.
 */
export const USAGE_READING_TTL_MS = 5 * 60 * 60 * 1000;

/** The shape a routine sends. Accepts `rate_limit_info` verbatim, or snake_case. */
export interface ReportedUsage {
  status?: string | null;
  rateLimitType?: string | null;
  rate_limit_type?: string | null;
  window?: string | null;
  resetsAt?: number | string | null;
  resets_at?: number | string | null;
  utilization?: number | null;
  isUsingOverage?: boolean | null;
  is_using_overage?: boolean | null;
}

function coerceUsageStatus(value: unknown): UsageStatus | null {
  if (typeof value !== 'string') return null;
  const s = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (s === 'allowed' || s === 'ok') return 'allowed';
  if (s === 'allowed_warning' || s === 'warning') return 'allowed_warning';
  if (s === 'rejected' || s === 'limited' || s === 'exhausted') return 'rejected';
  return null;
}

/** Unix seconds (what the session record holds), unix ms, or an ISO string. */
function coerceResetsAt(value: unknown): string | null {
  let ms: number | null = null;
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    ms = value < 1e12 ? value * 1000 : value;
  } else if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    ms = Number.isFinite(n) ? (n < 1e12 ? n * 1000 : n) : Date.parse(value);
  }
  return ms !== null && Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/** A fraction, or a percentage written as one — 0.42 and 42 both mean 42%. */
function coerceUtilization(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  const fraction = value > 1 ? value / 100 : value;
  return Math.min(1, fraction);
}

/**
 * What a report's `usage` field says, as windows. A reading whose status is
 * not one of the three is dropped rather than failing the report — the report
 * carries the routine's heartbeat, and losing that over a usage field would be
 * the worse trade.
 */
export function normaliseUsage(
  reported: ReportedUsage | ReportedUsage[] | null | undefined,
  now: Date = new Date(),
): UsageWindow[] {
  if (!reported) return [];
  const list = Array.isArray(reported) ? reported : [reported];
  const out: UsageWindow[] = [];
  for (const r of list) {
    if (!r || typeof r !== 'object') continue;
    const status = coerceUsageStatus(r.status);
    if (!status) continue;
    const window = (r.rateLimitType ?? r.rate_limit_type ?? r.window ?? '').toString().trim() || 'unknown';
    out.push({
      window: window.slice(0, 60),
      status,
      resets_at: coerceResetsAt(r.resetsAt ?? r.resets_at),
      utilization: coerceUtilization(r.utilization),
      overage: typeof (r.isUsingOverage ?? r.is_using_overage) === 'boolean'
        ? Boolean(r.isUsingOverage ?? r.is_using_overage)
        : null,
      reported_at: now.toISOString(),
    });
  }
  return out;
}

/** Is this reading still describing the window as it is now? */
export function isUsageLive(reading: UsageWindow, now: number): boolean {
  if (reading.resets_at) return Date.parse(reading.resets_at) > now;
  return now - Date.parse(reading.reported_at) < USAGE_READING_TTL_MS;
}

/** Fold new readings into what is stored, dropping windows that have rolled over. */
export function mergeUsage(
  existing: AgentUsage | null | undefined,
  readings: UsageWindow[],
  now: number = Date.now(),
): AgentUsage {
  const merged: AgentUsage = {};
  for (const [key, reading] of Object.entries(existing ?? {})) {
    if (reading && isUsageLive(reading, now)) merged[key] = reading;
  }
  for (const reading of readings) merged[reading.window] = reading;
  return merged;
}

/**
 * How much room a routine has, coarsely enough to sort by.
 *
 * `tier` is the decision: 2 is fine (or nothing reported), 1 is near the limit
 * or already spending overage, 0 is out. `used` breaks ties inside a tier when
 * a figure exists, in whole tenths so that a routine at 31% and one at 33% are
 * the same to the rotation rather than one of them taking everything. With no
 * figure — which is the usual case, since the session record carries none —
 * the tier's own midpoint stands in, so routines that only report a status
 * still share work by rotation.
 */
export interface UsageCapacity {
  tier: 0 | 1 | 2;
  used: number;
  /** The worst live window, for saying why. Null when nothing live is known. */
  limiting: UsageWindow | null;
  /** For tier 0: when the last rejected window rolls over. */
  back_at: string | null;
}

const TIER_OF: Record<UsageStatus, 0 | 1 | 2> = { allowed: 2, allowed_warning: 1, rejected: 0 };

/**
 * Used this much of a window and the subscription counts as full, whatever its
 * status still says. A subscription at 97% of its week reports
 * `allowed_warning` — "about to run out" — and a task sent there is a task
 * that dies partway through on the limit. Rigg never sends one there.
 */
export const USAGE_FULL_AT = 0.95;
const UNKNOWN_USED: Record<0 | 1 | 2, number> = { 2: 0.5, 1: 0.9, 0: 1 };

export function usageCapacity(
  agent: Pick<Agent, 'usage'>,
  now: number = Date.now(),
): UsageCapacity {
  const live = Object.values(agent.usage ?? {}).filter((r) => r && isUsageLive(r, now));
  if (live.length === 0) return { tier: 2, used: UNKNOWN_USED[2], limiting: null, back_at: null };

  const tierOf = (r: UsageWindow) =>
    (r.utilization !== null && r.utilization >= USAGE_FULL_AT && !r.overage
      ? 0
      : Math.min(TIER_OF[r.status], r.overage ? 1 : 2)) as 0 | 1 | 2;
  const tier = Math.min(...live.map(tierOf)) as 0 | 1 | 2;
  const atTier = live.filter((r) => tierOf(r) === tier);
  const known = live.map((r) => r.utilization).filter((u): u is number => u !== null);
  const used = Math.round((known.length ? Math.max(...known) : UNKNOWN_USED[tier]) * 10) / 10;
  const limiting = [...atTier].sort((a, b) => (b.utilization ?? 0) - (a.utilization ?? 0))[0] ?? null;

  const backAt =
    tier === 0
      ? atTier
          .map((r) => r.resets_at)
          .filter((t): t is string => Boolean(t))
          .sort()
          .at(-1) ?? null
      : null;

  return { tier, used, limiting, back_at: backAt };
}

/**
 * Who gets the next task: the routine with the most room on its subscription,
 * and among routines with the same room, whoever was least recently sent to.
 *
 * A routine that is out is sorted last rather than left out. Its reading is
 * only as fresh as its last report, and refusing to send anything would leave
 * the task sitting undispatched with nobody to retry it — so when everybody is
 * out, the work goes to whoever comes back first.
 */
type Rankable = Pick<Agent, 'usage' | 'dispatch_seq' | 'created_at'> &
  Partial<Pick<Agent, 'last_result' | 'last_dispatch_at'>>;

/**
 * How long a routine that refused a dispatch is passed over for one that did
 * not. Long enough that the queue's next retry goes somewhere else; short
 * enough that a blip does not bench a routine for the afternoon.
 */
export const DISPATCH_REFUSAL_COOLDOWN_MS = 10 * 60 * 1000;

export function recentlyRefused(agent: Partial<Pick<Agent, 'last_result' | 'last_dispatch_at'>>, now: number): boolean {
  return (
    agent.last_result === 'error' &&
    Boolean(agent.last_dispatch_at) &&
    now - Date.parse(agent.last_dispatch_at!) < DISPATCH_REFUSAL_COOLDOWN_MS
  );
}

export function compareByCapacity(a: Rankable, b: Rankable, now: number = Date.now()): number {
  const ca = usageCapacity(a, now);
  const cb = usageCapacity(b, now);
  // Out of quota is last whatever else is true. Above that, a routine that
  // just refused a dispatch gives way to any that did not — otherwise the
  // queue would retry the same broken endpoint every minute while a healthy
  // one sat idle.
  if ((ca.tier === 0) !== (cb.tier === 0)) return ca.tier === 0 ? 1 : -1;
  const ra = recentlyRefused(a, now);
  const rb = recentlyRefused(b, now);
  if (ra !== rb) return ra ? 1 : -1;
  if (ca.tier !== cb.tier) return cb.tier - ca.tier;
  if (ca.tier === 0) {
    const back = (ca.back_at ?? '').localeCompare(cb.back_at ?? '');
    if (back !== 0) return back;
  }
  if (ca.used !== cb.used) return ca.used - cb.used;
  return a.dispatch_seq - b.dispatch_seq || a.created_at.localeCompare(b.created_at);
}

/**
 * A Claude subscription routines run on — see the subscriptions migration.
 * Its key lives in `subscription_secrets`, out of reach of any browser;
 * `token_hint` is all the screen ever sees of it.
 */
export interface Subscription {
  id: string;
  /** The project it was added in, whose admins manage it. */
  project_id: string;
  /** Other projects that run routines on it too, and read its usage. */
  shared_project_ids: string[];
  name: string;
  usage: AgentUsage;
  token_hint: string | null;
  last_polled_at: string | null;
  last_poll_error: string | null;
  created_at: string;
  updated_at: string;
}

/** Whether routines in this project may run on the subscription — its own, or shared with it. */
export function subscriptionUsableIn(
  subscription: Pick<Subscription, 'project_id' | 'shared_project_ids'>,
  projectId: string,
): boolean {
  return subscription.project_id === projectId || (subscription.shared_project_ids ?? []).includes(projectId);
}

/**
 * The routine as auto-pick should see it: carrying its subscription's usage
 * when it has one. The subscription is what holds the limit, so its reading —
 * polled hourly — is the one that counts, whatever a routine said on its own.
 */
export function withSubscriptionUsage<T extends Pick<Agent, 'usage' | 'subscription_id'>>(
  agent: T,
  subscriptions: Record<string, Pick<Subscription, 'usage'>>,
): T {
  const subscription = agent.subscription_id ? subscriptions[agent.subscription_id] : undefined;
  return subscription ? { ...agent, usage: subscription.usage ?? {} } : agent;
}

const WINDOW_ORDER = ['five_hour', 'seven_day', 'seven_day_opus', 'seven_day_sonnet'];

/** Windows still in force, shortest first — what a subscription's card lists. */
export function liveWindows(usage: AgentUsage | null | undefined, now: number = Date.now()): UsageWindow[] {
  const rank = (w: string) => WINDOW_ORDER.indexOf(w) + 1 || 99;
  return Object.values(usage ?? {})
    .filter((r): r is UsageWindow => Boolean(r) && isUsageLive(r, now))
    .sort((a, b) => rank(a.window) - rank(b.window) || a.window.localeCompare(b.window));
}

/** The newest reading of any kind, live or rolled over. Null: never reported. */
export function lastReportedAt(usage: AgentUsage | null | undefined): string | null {
  let last: string | null = null;
  for (const reading of Object.values(usage ?? {})) {
    if (reading && (!last || reading.reported_at > last)) last = reading.reported_at;
  }
  return last;
}

/** `5h` → `five_hour`, `7d` → `seven_day`, `7d_opus` → `seven_day_opus`. */
function windowFromClaim(claim: string): string {
  const named = claim.replace(/^5h/, 'five_hour').replace(/^7d/, 'seven_day');
  return named.replace(/-/g, '_');
}

/**
 * A subscription's limits, read off the headers of any model call it made.
 *
 * Every answer to a subscription token carries, per window,
 * `anthropic-ratelimit-unified-<claim>-status`, `-utilization` (a fraction)
 * and `-reset` (unix seconds) — `5h`, `7d`, and per-model weekly claims when
 * they apply. `overage` is not a window: it says whether paid extra usage can
 * carry on past a window's limit, which turns that window's `rejected` into
 * "still working, at a cost".
 */
export function usageFromRateLimitHeaders(
  headers: { get(name: string): string | null } | Record<string, string>,
  now: Date = new Date(),
  reportedBy: string | null = null,
): UsageWindow[] {
  const entries: [string, string][] =
    typeof (headers as { get?: unknown }).get === 'function' && typeof (headers as Headers).forEach === 'function'
      ? (() => {
          const list: [string, string][] = [];
          (headers as Headers).forEach((value, key) => list.push([key.toLowerCase(), value]));
          return list;
        })()
      : Object.entries(headers as Record<string, string>).map(([k, v]) => [k.toLowerCase(), v]);

  const prefix = 'anthropic-ratelimit-unified-';
  const claims = new Map<string, { status?: string; reset?: string; utilization?: string }>();
  for (const [key, value] of entries) {
    if (!key.startsWith(prefix)) continue;
    const match = /^(.+)-(status|reset|utilization)$/.exec(key.slice(prefix.length));
    if (!match) continue;
    const [, claim, field] = match as unknown as [string, string, 'status' | 'reset' | 'utilization'];
    if (claim === 'overage') continue;
    claims.set(claim, { ...claims.get(claim), [field]: value });
  }
  const overageAllowed =
    entries.find(([k]) => k === `${prefix}overage-status`)?.[1]?.trim().toLowerCase() === 'allowed';

  const out: UsageWindow[] = [];
  for (const [claim, fields] of claims) {
    let status = coerceUsageStatus(fields.status);
    if (!status) continue;
    let overage: boolean | null = false;
    if (status === 'rejected' && overageAllowed) {
      status = 'allowed';
      overage = true;
    }
    out.push({
      window: windowFromClaim(claim).slice(0, 60),
      status,
      resets_at: coerceResetsAt(fields.reset),
      utilization: coerceUtilization(fields.utilization === undefined ? null : Number(fields.utilization)),
      overage,
      reported_at: now.toISOString(),
      reported_by: reportedBy,
    });
  }
  return out;
}

export function pickByCapacity<T extends Rankable>(
  agents: T[],
  now: number = Date.now(),
): T | null {
  return [...agents].sort((a, b) => compareByCapacity(a, b, now))[0] ?? null;
}

/**
 * Headers a provider's endpoint requires beyond authentication.
 *
 * Anthropic's Claude Code routine trigger refuses the request without
 * `anthropic-version`. The beta header is documented as required and is not
 * currently enforced — send it anyway, since the documented contract is the one
 * that will be.
 */
export function providerHeaders(provider: AgentProvider): Record<string, string> {
  switch (provider) {
    case 'anthropic':
      return {
        'anthropic-version': '2023-06-01',
        'anthropic-beta': 'experimental-cc-routine-2026-04-01',
      };
  }
}

/**
 * The URL of the run a dispatch just started, if the provider returns one.
 *
 * Anthropic's trigger answers with the session it created. Keeping it turns a
 * dispatch from a fire-and-hope into something you can watch: the task links
 * straight to the session doing the work. Everything else in that response —
 * the session id, the envelope type — Rigg has no use for.
 *
 * Deliberately total: a body that is not JSON, or is JSON of the wrong shape,
 * means no link. A dispatch the endpoint accepted must not be recorded as
 * failed because the reply was surprising.
 */
export function sessionUrlFromDispatch(provider: AgentProvider, body: string): string | null {
  if (!body) return null;

  switch (provider) {
    case 'anthropic': {
      try {
        const parsed = JSON.parse(body) as { claude_code_session_url?: unknown };
        const url = parsed?.claude_code_session_url;
        return typeof url === 'string' && url.startsWith('https://') ? url : null;
      } catch {
        return null;
      }
    }
  }
}

export interface Agent {
  id: string;
  project_id: string;
  repo_id: string | null;
  name: string;
  /** Where Rigg POSTs work. NULL for a `design` worker, which polls instead. */
  url: string | null;
  auth_header: string;
  auth_scheme: string | null;
  /** Which routine service this endpoint is. Decides the protocol headers. */
  provider: AgentProvider;
  /** Push (`routine`) or pull (`design`). A pull worker has no `url`. */
  kind: AgentKind;
  /** Last authenticated call from this routine — the pull worker's heartbeat. */
  last_seen_at: string | null;
  /** Short sha of a design worker's own checkout, as of its last poll. */
  worker_version: string | null;
  /** Commits that worker is behind its upstream. 0 is current, null unknown. */
  worker_behind: number | null;
  /** Its checkout has local changes, so it is not pulling over them. */
  worker_dirty: boolean | null;
  notes: string | null;
  /** Excluded from round-robin auto-pick while true; can still be targeted by id. */
  paused: boolean;
  /** `normal` (default) or `smart` — which level of task auto-pick sends it. */
  intelligence: AgentIntelligence;
  /**
   * The Claude subscription it runs on. Routines on one share one limit, and
   * that subscription's usage — not this column's — is what auto-pick reads.
   */
  subscription_id: string | null;
  /**
   * Rate-limit windows a routine reported itself, for one with no
   * subscription. See `usageCapacity` and `withSubscriptionUsage`.
   */
  usage: AgentUsage;
  dispatch_count: number;
  dispatch_seq: number;
  last_dispatch_at: string | null;
  last_result: 'ok' | 'error' | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: string;
  project_id: string;
  slug: string;
  label: string;
  color: string | null;
  created_by_agent_id: string | null;
  created_at: string;
}

export interface Profile {
  id: string;
  email: string | null;
  name: string;
  avatar_url: string | null;
  created_at: string;
}

export interface TaskComment {
  id: string;
  task_id: string;
  project_id: string;
  user_id: string | null;
  agent_id: string | null;
  author_name: string | null;
  origin: 'rigg' | 'agent' | 'github';
  body: string;
  github_url: string | null;
  github_id: number | null;
  github_error: string | null;
  created_at: string;
}

export interface TaskEvent {
  id: number;
  task_id: string;
  project_id: string;
  at: string;
  kind: string;
  actor: Actor;
  actor_user_id: string | null;
  actor_agent_id: string | null;
  status: TaskStatus | null;
  message: string | null;
  data: unknown;
}

/**
 * Whether a run is *the* production deploy having succeeded.
 *
 * Named rather than inferred: a production deploy is the rarest workflow a
 * repository has, and guessing at it from names like "deploy" would eventually
 * promote a task off the back of a staging run.
 */
export function isProductionRun(
  run: { name: string; status: string; conclusion: string | null },
  workflow: string | null | undefined,
): boolean {
  if (!workflow) return false;
  return run.name === workflow && run.status === 'completed' && run.conclusion === 'success';
}

/**
 * Whether a production deploy carries a given task.
 *
 * Merged is not deployed — that is the gap this closes. The test is whether the
 * deploy finished *after* the merge: one that started before it cannot contain
 * it. Timestamps rather than commit ancestry, because Rigg does not hold the
 * graph, and being one deploy conservative is the safe direction — a task that
 * shipped shows up as deployed on the next run, whereas the other error says
 * something is live when it is not.
 *
 * Only `merged` moves. A task still in review is not promoted by a deploy that
 * happens to run while it is open, and `deployed` is where the ladder ends.
 */
export function shippedBy(
  task: { status: TaskStatus; merged_at: string | null },
  completedAt: string | null,
): boolean {
  if (task.status !== 'merged' || !task.merged_at || !completedAt) return false;
  return completedAt > task.merged_at;
}

export interface CiRun {
  id: string;
  project_id: string;
  repo_id: string;
  provider: 'workflow_run' | 'check_run' | 'status';
  external_id: string;
  branch: string | null;
  head_sha: string;
  name: string;
  status: 'queued' | 'in_progress' | 'completed';
  conclusion: string | null;
  html_url: string | null;
  event: string | null;
  started_at: string | null;
  completed_at: string | null;
  updated_at: string;
}

/* ── questions and follow-ups ───────────────────────────────────────────── */

/**
 * A routine asking a person something it cannot decide alone.
 *
 * `choice` is picking one of `options`, with an "Other" box on top when
 * `allow_other` is set; `text` is a free-text answer. Asked in batches and
 * answered in batches, so the routine gets every answer in one comment — see
 * the `20261004140000_questions_and_follow_ups` migration.
 */
export const QUESTION_KINDS = ['choice', 'text'] as const;
export type QuestionKind = (typeof QUESTION_KINDS)[number];

/** More than this in one go is a questionnaire, not a question. */
export const QUESTIONS_PER_BATCH_MAX = 5;
export const QUESTION_OPTIONS_MAX = 6;
export const QUESTION_MAX_LENGTH = 300;
export const QUESTION_CONTEXT_MAX_LENGTH = 600;
export const QUESTION_OPTION_MAX_LENGTH = 160;
export const ANSWER_MAX_LENGTH = 4000;

export interface TaskQuestion {
  id: string;
  task_id: string;
  project_id: string;
  batch_id: string;
  position: number;
  agent_id: string | null;
  kind: QuestionKind;
  question: string;
  context: string | null;
  options: string[];
  allow_other: boolean;
  answer: string | null;
  answer_other: boolean;
  answered_by: string | null;
  answered_at: string | null;
  dismissed_at: string | null;
  created_at: string;
}

/** Still waiting on a person: neither answered nor replaced by a newer batch. */
export function isOpenQuestion(question: Pick<TaskQuestion, 'answered_at' | 'dismissed_at'>): boolean {
  return !question.answered_at && !question.dismissed_at;
}

/** One answer as a person submits it. */
export interface QuestionAnswer {
  question_id: string;
  /** For `choice`: one of the question's options, verbatim. */
  choice?: string | null;
  /** For `choice`: what was typed into "Other". For `text`: the answer. */
  text?: string | null;
}

/**
 * What to store for one answer, or why it cannot be accepted.
 *
 * A choice must be one of the options as offered — not a near miss — or, when
 * "Other" is allowed, a non-empty typed answer. Every question in a batch has
 * to be answered: half a batch is the trickle batching exists to avoid.
 */
export function resolveAnswer(
  question: Pick<TaskQuestion, 'kind' | 'options' | 'allow_other' | 'question'>,
  answer: QuestionAnswer | undefined,
): { answer: string; other: boolean } | { error: string } {
  const typed = answer?.text?.trim() ?? '';
  const picked = answer?.choice?.trim() ?? '';

  if (question.kind === 'text') {
    if (!typed) return { error: `"${question.question}" needs an answer` };
    return { answer: typed.slice(0, ANSWER_MAX_LENGTH), other: false };
  }

  if (picked) {
    if (!question.options.includes(picked)) {
      return { error: `"${picked}" is not one of the options for "${question.question}"` };
    }
    return { answer: picked, other: false };
  }
  if (typed) {
    if (!question.allow_other) {
      return { error: `"${question.question}" has to be answered with one of its options` };
    }
    return { answer: typed.slice(0, ANSWER_MAX_LENGTH), other: true };
  }
  return { error: `"${question.question}" needs an answer` };
}

/** Indent every line after the first, so Markdown keeps it inside a list item. */
function indented(text: string, prefix = '   '): string {
  return text
    .trim()
    .split('\n')
    .map((line) => `${prefix}${line}`)
    .join('\n');
}

/**
 * The comment posted to the pull request when a routine asks.
 *
 * Readable on its own, because GitHub is where some people will see it first —
 * but answered in Rigg, where the choices are buttons and the batch goes back
 * as one.
 */
export function questionsComment(
  questions: readonly Pick<TaskQuestion, 'kind' | 'question' | 'context' | 'options' | 'allow_other'>[],
  answerUrl: string,
): string {
  const lines = [
    questions.length === 1 ? '**A question for you**' : `**${questions.length} questions for you**`,
    '',
  ];
  questions.forEach((q, i) => {
    lines.push(`${i + 1}. **${q.question.trim()}**${q.kind === 'text' ? ' _(free text)_' : ''}`);
    if (q.context?.trim()) lines.push(indented(q.context));
    if (q.kind === 'choice') {
      for (const option of q.options) lines.push(`   - ${option}`);
      if (q.allow_other) lines.push('   - Other — write your own');
    }
  });
  lines.push('', `Answer them together in Rigg: ${answerUrl}`);
  return lines.join('\n');
}

/** The comment that carries the answers back to the routine, as one batch. */
export function answersComment(
  questions: readonly Pick<TaskQuestion, 'question' | 'answer' | 'answer_other'>[],
): string {
  const lines = [questions.length === 1 ? '**Answer to your question**' : '**Answers to your questions**', ''];
  questions.forEach((q, i) => {
    lines.push(`${i + 1}. **${q.question.trim()}**`);
    const answer = q.answer?.trim() || '(no answer)';
    lines.push(indented(q.answer_other ? `→ Other: ${answer}` : `→ ${answer}`));
  });
  return lines.join('\n');
}

/**
 * Work a routine suggested beyond its own task. Held as `pending` until the
 * task it came from merges, then `suggested` on the board until somebody
 * accepts or rejects it.
 */
export const FOLLOW_UP_STATES = ['pending', 'suggested', 'accepted', 'rejected'] as const;
export type FollowUpState = (typeof FOLLOW_UP_STATES)[number];

/** A routine suggesting more than a handful is writing a backlog, not follow-ups. */
export const FOLLOW_UPS_PER_TASK_MAX = 5;

export interface TaskFollowUp {
  id: string;
  task_id: string;
  project_id: string;
  agent_id: string | null;
  title: string;
  prompt: string;
  repo_id: string | null;
  category_id: string | null;
  /** The routine's suggestion; the person accepting it has the last word. */
  kind: TaskKind | null;
  auto_merge: boolean;
  smart: boolean;
  state: FollowUpState;
  suggested_at: string | null;
  decided_by: string | null;
  decided_at: string | null;
  created_task_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * The state a follow-up starts in: offered right away when the task it came
 * from has already landed, held until it does otherwise.
 */
export function initialFollowUpState(origin: Pick<Task, 'status'>): FollowUpState {
  return origin.status === 'merged' || origin.status === 'deployed' ? 'suggested' : 'pending';
}

/** The line appended to an accepted follow-up's prompt, so the routine knows where it came from. */
export function followUpProvenance(origin: Pick<Task, 'ref' | 'title' | 'pr_url'>): string {
  return (
    `This is a follow-up to task #${origin.ref} ("${origin.title}")` +
    (origin.pr_url ? `, which landed in ${origin.pr_url}` : '') +
    '.'
  );
}
